import { createTheme } from '@material-ui/core/styles'

// Design tokens — shared with desertFlora.css custom properties
const tokens = {
  // surfaces
  bg: '#f4efe4',
  bg2: '#ece5d3',
  surface: '#fbf8f1',
  surface2: '#f6f1e3',
  line: '#e0d8c2',
  line2: '#cec3a5',
  // ink
  ink: '#1c2419',
  ink2: '#3d463a',
  ink3: '#6f7a6b',
  ink4: '#97a094',
  // brand
  primary: '#1f5e3a',
  primary2: '#164a2c',
  primarySoft: '#e3eee0',
  accent: '#c66a45',
  accent2: '#a4502f',
  accentSoft: '#f7e4d6',
  // status
  danger: '#a9341d',
  dangerSoft: '#f7dccf',
  warn: '#8c5b00',
  warnSoft: '#fff3d9',
  info: '#3454c1',
  infoSoft: '#e6ebfa',
  ok: '#2f7a3e',
  okSoft: '#dceedf',
  // cost types
  labor: '#3454c1',
  laborSoft: '#e6ebfa',
  equipment: '#b35d1a',
  equipmentSoft: '#fbe8d6',
  material: '#2f7a3e',
  materialSoft: '#dceedf',
  subcontract: '#6e3fb7',
  subcontractSoft: '#ece2fa',
  disposal: '#a9341d',
  disposalSoft: '#f7dccf',
  // type
  fontUi: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontMono: "'Geist Mono', ui-monospace, 'JetBrains Mono', Menlo, monospace"
}

const desertFloraTheme = createTheme({
  palette: {
    primary: { main: tokens.primary, dark: tokens.primary2, light: tokens.primarySoft, contrastText: '#fbf8f1' },
    secondary: { main: tokens.accent, dark: tokens.accent2, light: tokens.accentSoft, contrastText: '#fff' },
    error: { main: tokens.danger },
    success: { main: tokens.ok },
    warning: { main: '#b35d1a' },
    background: { default: tokens.bg, paper: tokens.surface },
    text: { primary: tokens.ink, secondary: tokens.ink3, disabled: tokens.ink4 },
    divider: tokens.line
  },
  typography: {
    fontFamily: tokens.fontUi,
    fontSize: 13,
    h1: { fontSize: 24, fontWeight: 700, color: tokens.ink },
    h2: { fontSize: 20, fontWeight: 700, color: tokens.ink },
    h3: { fontSize: 18, fontWeight: 600, color: tokens.ink },
    h6: { fontSize: 16, fontWeight: 600, color: tokens.ink },
    body1: { fontSize: 14, color: tokens.ink },
    body2: { fontSize: 13, color: tokens.ink2 },
    caption: { fontSize: 12, color: tokens.ink3 },
    button: { fontSize: 13, fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    overline: {
      fontFamily: tokens.fontMono,
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: '.12em',
      textTransform: 'uppercase',
      color: tokens.ink3
    }
  },
  shape: { borderRadius: 10 },
  shadows: [
    'none',
    '0 1px 2px rgba(28,36,25,.06)',
    '0 2px 8px rgba(28,36,25,.08), 0 1px 2px rgba(28,36,25,.04)',
    ...Array(21).fill('0 12px 40px rgba(28,36,25,.16), 0 4px 12px rgba(28,36,25,.08)'),
    '0 24px 80px rgba(28,36,25,.22), 0 8px 24px rgba(28,36,25,.10)'
  ],
  overrides: {
    // ── Buttons ──────────────────────────────────────────────────────
    MuiButton: {
      root: {
        textTransform: 'none',
        fontFamily: tokens.fontUi,
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: 0,
        borderRadius: 10,
        padding: '9px 14px',
        transition: 'background .12s, border-color .12s, box-shadow .12s, transform .12s',
        '&:active': { transform: 'translateY(1px)' },
        // quiet destructive — apply via className="df-danger"
        '&.df-danger': {
          backgroundColor: 'transparent',
          color: tokens.ink3,
          border: '1px solid ' + tokens.line,
          '&:hover': { color: tokens.danger, borderColor: tokens.danger, backgroundColor: tokens.dangerSoft }
        },
        '&.df-danger-strong': {
          backgroundColor: tokens.danger,
          color: '#fff',
          border: '1px solid #8b2917',
          '&:hover': { backgroundColor: '#8b2917' }
        }
      },
      sizeSmall: { padding: '6px 10px', fontSize: 12, borderRadius: 8 },
      outlined: {
        backgroundColor: tokens.surface,
        borderColor: tokens.line2,
        color: tokens.ink,
        '&:hover': { backgroundColor: tokens.surface2, borderColor: '#b8aa86' }
      },
      text: {
        color: tokens.ink2,
        '&:hover': { backgroundColor: 'rgba(28,36,25,.05)' }
      },
      contained: {
        boxShadow: '0 1px 2px rgba(28, 36, 25, 0.06)',
        '&:hover': { boxShadow: '0 2px 8px rgba(28, 36, 25, 0.08)' }
      },
      containedPrimary: {
        backgroundColor: tokens.primary,
        color: '#fbf8f1',
        border: '1px solid ' + tokens.primary2,
        boxShadow: '0 1px 0 rgba(255,255,255,.08) inset, 0 4px 14px rgba(31,94,58,.25)',
        '&:hover': { backgroundColor: tokens.primary2 }
      },
      containedSecondary: {
        backgroundColor: tokens.accent,
        color: '#fff',
        border: '1px solid ' + tokens.accent2,
        boxShadow: '0 4px 14px rgba(198,106,69,.25)',
        '&:hover': { backgroundColor: tokens.accent2 }
      }
    },

    // ── Inputs (static labels, sand surface) ────────────────────────
    MuiTextField: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          backgroundColor: tokens.surface,
          fontSize: 13,
          transition: 'background .12s, box-shadow .12s',
          '& fieldset': { borderColor: tokens.line, transition: 'border-color .12s' },
          '&:hover fieldset': { borderColor: tokens.line2 },
          '&.Mui-focused': {
            backgroundColor: tokens.surface,
            boxShadow: '0 0 0 3px rgba(31,94,58,.18)'
          },
          '&.Mui-focused fieldset': { borderColor: tokens.primary + ' !important', borderWidth: '1px !important' }
        },
        '& .MuiOutlinedInput-input': {
          padding: '9px 12px',
          height: 'auto'
        },
        '& .MuiOutlinedInput-multiline': {
          padding: 0
        },
        // Static label above input
        '& .MuiInputLabel-outlined': {
          position: 'static',
          transform: 'none !important',
          fontSize: 12,
          fontWeight: 600,
          color: tokens.ink2,
          marginBottom: 6,
          '&.Mui-focused': { color: tokens.primary }
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
          transform: 'none !important'
        },
        // Hide the notch that MUI creates for the floating label
        '& .MuiOutlinedInput-notchedOutline legend': {
          display: 'none'
        }
      }
    },
    MuiInputLabel: {
      outlined: {
        position: 'static',
        transform: 'none !important',
        fontSize: 12,
        fontWeight: 600,
        color: tokens.ink2,
        marginBottom: 6,
        '&.Mui-focused': { color: tokens.primary },
        '&.MuiInputLabel-shrink': {
          transform: 'none !important'
        }
      }
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 8,
        backgroundColor: tokens.surface,
        fontSize: 13,
        transition: 'background .12s, box-shadow .12s',
        '& fieldset': { borderColor: tokens.line, transition: 'border-color .12s' },
        '&:hover fieldset': { borderColor: tokens.line2 },
        '&.Mui-focused': {
          backgroundColor: tokens.surface,
          boxShadow: '0 0 0 3px rgba(31,94,58,.18)'
        },
        '&.Mui-focused fieldset': { borderColor: tokens.primary + ' !important', borderWidth: '1px !important' }
      },
      input: {
        padding: '9px 12px',
        height: 'auto'
      },
      multiline: {
        padding: '9px 12px'
      },
      inputMultiline: {
        padding: 0
      },
      notchedOutline: {
        '& legend': { display: 'none' }
      }
    },
    MuiFormHelperText: {
      root: {
        marginLeft: 0,
        marginTop: 6,
        fontSize: 12,
        color: tokens.ink3
      }
    },

    // ── Dialogs (warm surface, bordered header + footer) ────────────
    MuiDialog: {
      paper: {
        backgroundColor: tokens.surface,
        borderRadius: 20,
        boxShadow: '0 24px 80px rgba(28,36,25,.22), 0 8px 24px rgba(28,36,25,.10)'
      }
    },
    MuiDialogTitle: {
      root: {
        padding: '18px 24px',
        borderBottom: '1px solid ' + tokens.line,
        fontWeight: 700,
        fontSize: 18,
        color: tokens.ink
      }
    },
    MuiDialogContent: {
      root: {
        padding: '20px 24px'
      },
      dividers: {
        borderTop: 'none',
        borderBottom: 'none'
      }
    },
    MuiDialogActions: {
      root: {
        padding: '14px 20px',
        borderTop: '1px solid ' + tokens.line,
        backgroundColor: tokens.bg2,
        gap: 8
      }
    },
    MuiBackdrop: {
      root: {
        backgroundColor: 'rgba(28,36,25,.45)',
        backdropFilter: 'blur(4px)'
      }
    },

    // ── Chips (pill, mono uppercase, bordered, leading dot) ─────────
    MuiChip: {
      root: {
        height: 22,
        borderRadius: 999,
        fontFamily: tokens.fontMono,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        border: '1px solid currentColor',
        backgroundColor: tokens.surface2,
        color: tokens.ink2,
        paddingLeft: 7,
        paddingRight: 8,
        // leading dot
        '&::before': {
          content: '""',
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: 'currentColor',
          marginRight: 6,
          flexShrink: 0
        },
        // tone variants — apply via className
        '&.df-chip-ok': { color: tokens.ok, backgroundColor: tokens.okSoft },
        '&.df-chip-warn': { color: tokens.warn, backgroundColor: tokens.warnSoft },
        '&.df-chip-info': { color: tokens.info, backgroundColor: tokens.infoSoft },
        '&.df-chip-danger': { color: tokens.danger, backgroundColor: tokens.dangerSoft },
        '&.df-chip-labor': { color: tokens.info, backgroundColor: tokens.infoSoft },
        '&.df-chip-equipment': { color: '#b35d1a', backgroundColor: '#fbe8d6' },
        '&.df-chip-material': { color: tokens.ok, backgroundColor: tokens.okSoft },
        '&.df-chip-subcontract': { color: '#6e3fb7', backgroundColor: '#ece2fa' },
        '&.df-chip-disposal': { color: tokens.danger, backgroundColor: tokens.dangerSoft }
      },
      sizeSmall: {
        height: 22,
        fontSize: 10
      },
      label: {
        padding: 0
      }
    },

    // ── Tabs ─────────────────────────────────────────────────────────
    MuiTabs: {
      root: {
        minHeight: 40
      },
      indicator: {
        backgroundColor: tokens.primary,
        height: 2
      }
    },
    MuiTab: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 13,
        minHeight: 40,
        minWidth: 100,
        color: tokens.ink3,
        '&.Mui-selected': { color: tokens.ink }
      }
    },

    // ── Switch ───────────────────────────────────────────────────────
    MuiSwitch: {
      root: {
        width: 52,
        height: 32,
        padding: 6
      },
      switchBase: {
        padding: 7,
        color: '#fff',
        '&.Mui-checked': {
          transform: 'translateX(20px)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            backgroundColor: tokens.primary,
            opacity: 1
          }
        }
      },
      thumb: {
        width: 16,
        height: 16,
        boxShadow: '0 1px 2px rgba(0,0,0,.18)'
      },
      track: {
        borderRadius: 999,
        backgroundColor: tokens.bg2,
        border: '1px solid ' + tokens.line2,
        opacity: 1
      }
    },

    // ── AppBar ───────────────────────────────────────────────────────
    MuiAppBar: {
      root: {
        boxShadow: 'none'
      },
      colorPrimary: {
        backgroundColor: tokens.primary,
        backgroundImage: 'none'
      }
    },

    // ── Paper / Cards ────────────────────────────────────────────────
    MuiPaper: {
      root: {
        backgroundColor: tokens.surface,
        backgroundImage: 'none',
        border: '1px solid ' + tokens.line
      },
      rounded: {
        borderRadius: 14
      },
      elevation0: { boxShadow: 'none' },
      elevation1: { boxShadow: '0 1px 2px rgba(28, 36, 25, 0.06)' },
      elevation2: { boxShadow: '0 2px 8px rgba(28,36,25,.08), 0 1px 2px rgba(28,36,25,.04)' }
    },

    // ── Tables ───────────────────────────────────────────────────────
    MuiTableHead: {
      root: {
        '& .MuiTableCell-head': {
          fontFamily: tokens.fontMono,
          fontWeight: 600,
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: tokens.ink3,
          borderBottom: '2px solid ' + tokens.line,
          backgroundColor: tokens.surface2
        }
      }
    },
    MuiTableCell: {
      root: {
        borderBottom: '1px solid ' + tokens.line,
        fontSize: 13,
        padding: '10px 12px'
      }
    },
    MuiTableRow: {
      root: {
        '&.MuiTableRow-hover:hover': {
          backgroundColor: 'rgba(31, 94, 58, 0.04)'
        }
      }
    },

    // ── Dropdowns / Popovers ──────────────────────────────────────────
    MuiPopover: {
      paper: {
        backgroundColor: tokens.surface,
        border: '1px solid ' + tokens.line
      }
    },
    MuiMenu: {
      paper: {
        backgroundColor: tokens.surface
      },
      list: {
        '& .MuiMenuItem-root': {
          fontSize: 13,
          '&:hover': { backgroundColor: tokens.surface2 },
          '&.Mui-selected': { backgroundColor: tokens.primarySoft },
          '&.Mui-selected:hover': { backgroundColor: tokens.primarySoft }
        }
      }
    },
    MuiAutocomplete: {
      paper: {
        backgroundColor: tokens.surface,
        border: '1px solid ' + tokens.line,
        boxShadow: '0 4px 12px rgba(28,36,25,.12)'
      },
      option: {
        fontSize: 13,
        '&[data-focus="true"]': { backgroundColor: tokens.surface2 },
        '&[aria-selected="true"]': { backgroundColor: tokens.primarySoft }
      }
    },
    MuiDialog: {
      paper: {
        backgroundColor: tokens.surface
      }
    },

    // ── Other controls ───────────────────────────────────────────────
    MuiSelect: {
      root: { fontSize: 13 }
    },
    MuiIconButton: {
      root: {
        color: tokens.ink3,
        '&:hover': { backgroundColor: 'rgba(31, 94, 58, 0.08)' }
      }
    },
    MuiCheckbox: {
      colorPrimary: {
        color: tokens.ink4,
        '&.Mui-checked': { color: tokens.primary }
      }
    }
  }
})

export { tokens }
export default desertFloraTheme
